/**
 * Share one in-flight async result across a burst of callers.
 *
 * Several admin pages independently ask "who is the signed-in staff member and
 * what can they do" many times while loading a single screen. On the
 * per-service application list that question is answered from scratch by three
 * concurrent fetch chains and again by each of the queries they make, so the
 * same `auth.getUser()` and the same `es_users` row are fetched a dozen times
 * within about two seconds. None of those answers can differ from each other.
 *
 * This wraps such a function so that concurrent callers share one promise, and
 * a settled answer is reused for a short window afterwards. The window is
 * deliberately short: it exists to collapse one page load, not to cache
 * identity across a session, so a role change takes effect almost immediately
 * rather than lingering.
 *
 * Failures are never cached. Neither is a result the caller declares
 * uncacheable, which matters for functions that swallow their own errors and
 * return a degraded value instead of rejecting.
 */

export interface MemoizedBriefly<Args extends unknown[], Result> {
  (...args: Args): Promise<Result>;
  /** Drop everything held. Call when the identity behind the cache changes. */
  invalidate(): void;
}

export interface MemoizeBrieflyOptions<Args extends unknown[], Result> {
  /** How long a settled result stays usable, in milliseconds. */
  ttlMs: number;
  /** Cache key. Callers with the same key share an answer. */
  key: (...args: Args) => string;
  /**
   * Return false to evict a settled result immediately. Use it for degraded
   * fallbacks that a function returns instead of throwing.
   */
  cacheable?: (result: Result) => boolean;
  /** Injectable clock, for tests. */
  now?: () => number;
}

interface Entry<Result> {
  promise: Promise<Result>;
  /** Unset while in flight, so a slow call cannot expire before it settles. */
  settledAt: number | null;
}

export function memoizeBriefly<Args extends unknown[], Result>(
  fn: (...args: Args) => Promise<Result>,
  options: MemoizeBrieflyOptions<Args, Result>,
): MemoizedBriefly<Args, Result> {
  const { ttlMs, key, cacheable, now = Date.now } = options;
  const entries = new Map<string, Entry<Result>>();

  const memoized = ((...args: Args): Promise<Result> => {
    const cacheKey = key(...args);
    const existing = entries.get(cacheKey);

    if (existing) {
      // In flight: join it. Settled and fresh: reuse it.
      if (existing.settledAt === null || now() - existing.settledAt < ttlMs) {
        return existing.promise;
      }
      entries.delete(cacheKey);
    }

    const promise = fn(...args);
    const entry: Entry<Result> = { promise, settledAt: null };
    entries.set(cacheKey, entry);

    promise.then(
      (result) => {
        // Only touch the map if this entry is still the current one; a call to
        // invalidate() during the request must not be undone by its arrival.
        if (entries.get(cacheKey) !== entry) return;
        if (cacheable && !cacheable(result)) {
          entries.delete(cacheKey);
          return;
        }
        entry.settledAt = now();
      },
      () => {
        if (entries.get(cacheKey) === entry) {
          entries.delete(cacheKey);
        }
      },
    );

    return promise;
  }) as MemoizedBriefly<Args, Result>;

  memoized.invalidate = () => {
    entries.clear();
  };

  return memoized;
}
